/**
 * PrepaidUntilBadge — ป้าย "ชำระล่วงหน้าถึง …" บนการ์ดข้อมูลการเช่า (หน้ารายละเอียดสัญญา)
 *
 * ลูกค้าจ่ายค่าเช่าล่วงหน้าแล้วถึงวันที่นี้ → วางบิลรายเดือนจะข้ามสัญญานี้ (ดู rentals.prepaid_until)
 * ไม่มีค่า = ไม่แสดงอะไรเลย · วันที่ผ่านไปแล้ว = ป้ายเทา "สิ้นสุดแล้ว" (ยังเก็บไว้เป็นประวัติว่าเคยจ่ายล่วงหน้า)
 */
import React from 'react';
import { RiWalletLine } from 'react-icons/ri';
import { cn } from '../../utils/cn';

export interface PrepaidUntilBadgeProps {
  prepaidUntil?: string | null;
  prepaidNote?: string | null;
  /** ทดสอบได้: ระบุ "วันนี้" (YYYY-MM-DD) ไม่งั้นใช้เวลาเครื่อง */
  today?: string;
}

const datePart = (v?: string | null): string => (v ? String(v).slice(0, 10) : '');

const formatThaiDate = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('th-TH', { year: '2-digit', month: 'long', day: 'numeric' });
};

const localToday = (): string => {
  const n = new Date();
  const mm = String(n.getMonth() + 1).padStart(2, '0');
  const dd = String(n.getDate()).padStart(2, '0');
  return `${n.getFullYear()}-${mm}-${dd}`;
};

const PrepaidUntilBadge: React.FC<PrepaidUntilBadgeProps> = ({ prepaidUntil, prepaidNote, today }) => {
  const until = datePart(prepaidUntil);
  if (!until) return null;
  const expired = until < (today || localToday());
  return (
    <div data-testid="prepaid-until-badge" className="mt-1">
      <span
        className={cn(
          'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium',
          expired ? 'bg-secondary-100 text-secondary-600' : 'bg-success-100 text-success-700'
        )}
        title="ลูกค้าชำระค่าเช่าล่วงหน้าแล้ว — วางบิลรายเดือนจะข้ามรอบที่ครอบคลุม ไม่ออกใบแจ้งหนี้ซ้ำ"
      >
        <RiWalletLine className="h-3.5 w-3.5" />
        ชำระล่วงหน้าถึง {formatThaiDate(until)}
        {expired && <span> (สิ้นสุดแล้ว)</span>}
      </span>
      {prepaidNote && (
        <p data-testid="prepaid-until-note" className="mt-1 text-xs text-secondary-500 break-words">{prepaidNote}</p>
      )}
    </div>
  );
};

export default PrepaidUntilBadge;
